import { Controller } from "@hotwired/stimulus"

// One helper for every "something changed, tell the person" moment (docs/07
// Cross-cutting, design review issue 18). Six situations, one mechanism:
//
//   step change          focus the new step heading, announce "Step 2 of 4, Booths"
//   validation error     focus the error summary, whose links jump to fields
//   booth conflict       focus the picker heading, announce the taken booth
//   queue auto-advance   focus the detail heading, announce what opened
//   dialogs and menus    move focus in, trap, return to the trigger on close
//   Turbo visits         focus the page h1
//
// Written once because these are the same two actions every time — move focus,
// say what happened — and six separate implementations means five of them are
// subtly wrong and nobody notices, since the people who would notice are the
// ones least likely to be asked.
//
// Shared between admin and public, so it carries no colours (DESIGN.md).
export default class extends Controller {
  static targets = ["region"]

  connect() {
    this.announceOnVisit = this.announceOnVisit.bind(this)
    this.noteVisit = this.noteVisit.bind(this)
    document.addEventListener("turbo:visit", this.noteVisit)
    document.addEventListener("turbo:load", this.announceOnVisit)
    document.addEventListener("focus-announcer:announce", this.handleEvent)
  }

  disconnect() {
    document.removeEventListener("turbo:visit", this.noteVisit)
    document.removeEventListener("turbo:load", this.announceOnVisit)
    document.removeEventListener("focus-announcer:announce", this.handleEvent)
  }

  // `turbo:load` fires on the FIRST render as well as after each visit, and
  // moving focus on the first one puts a keyboard user inside the content
  // before they have met the nav or the skip link — their first Tab lands on
  // the second link of the page. `turbo:visit` only fires for a real
  // navigation, so it is what tells the two apart (P8.3).
  noteVisit() { this.navigated = true }

  handleEvent = (event) => {
    const { message, focus } = event.detail ?? {}
    if (focus) this.moveFocusTo(document.querySelector(focus))
    if (message) this.say(message)
  }

  // A live region only announces content inserted AFTER it is in the
  // document, so the region ships empty in the layout and is filled here.
  // Clearing first makes a repeated message announce again: two identical
  // booth conflicts in a row would otherwise be silent the second time.
  say(message) {
    if (!this.hasRegionTarget) return

    this.regionTarget.textContent = ""
    requestAnimationFrame(() => { this.regionTarget.textContent = message })
  }

  moveFocusTo(element) {
    if (!element) return

    // Headings are not focusable by default; tabindex="-1" makes them
    // programmatically focusable without adding them to the tab order.
    if (!element.hasAttribute("tabindex")) element.setAttribute("tabindex", "-1")
    element.focus({ preventScroll: false })
  }

  // After a Turbo visit the browser keeps focus where it was, which on a
  // navigation means focus is on a link that no longer exists and a screen
  // reader announces nothing at all.
  announceOnVisit() {
    if (!this.navigated) return

    const heading = document.querySelector("main h1")
    if (heading) this.moveFocusTo(heading)
  }
}
